import React, { useMemo } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  Activity,
  ArrowLeft,
  CalendarDays,
  Clock3,
  Flag,
  MapPin,
  Radio,
  Signal,
  Trophy,
  Users,
} from "lucide-react";
import { useOfficialMatch } from "@/queries/matches";
import { describeMatchStart, getMatchTeams, getMatchVenue } from "@/utils/matchFormatting";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";

function normalizeLiveStatus(match, startInfo) {
  const raw = String(match?.status || "").toLowerCase();
  if (raw === "live") return "live";
  if (raw === "completed" || raw === "finished") return "finished";
  if (startInfo?.isPast) return "finished";
  return "upcoming";
}

function toNumber(value, fallback = 0) {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : fallback;
}

function asLineupList(lineup) {
  if (!lineup) return [];
  if (Array.isArray(lineup)) return lineup;
  if (Array.isArray(lineup?.riders)) return lineup.riders;
  if (Array.isArray(lineup?.entries)) return lineup.entries;
  return [];
}

function getLiveDetailModel(match) {
  const startInfo = describeMatchStart(match);
  const { home, away } = getMatchTeams(match);
  const venue = getMatchVenue(match);
  const live = match?.live || match?.live_data || match?.liveData || {};
  const homeLineup = asLineupList(match?.home_lineup || match?.homeLineup || live?.homeLineup);
  const awayLineup = asLineupList(match?.away_lineup || match?.awayLineup || live?.awayLineup);
  const completedHeats = toNumber(
    live?.completedHeats ?? live?.heatsCompleted ?? live?.completed_heats ?? match?.completed_heats ?? 0
  );
  const totalHeats = Math.max(1, toNumber(live?.totalHeats ?? live?.total_heats ?? match?.total_heats ?? 15, 15));
  const currentHeat = toNumber(live?.currentHeat ?? live?.current_heat ?? live?.heatNumber ?? 0);
  const status = normalizeLiveStatus(match, startInfo);

  return {
    home,
    away,
    venue,
    startInfo,
    status,
    statusText:
      live?.statusText ||
      live?.phaseLabel ||
      (status === "live" ? "Pågående" : status === "finished" ? "Avslutad" : "Kommande"),
    homeScore: live?.homeScore ?? live?.home_score ?? match?.home_score ?? null,
    awayScore: live?.awayScore ?? live?.away_score ?? match?.away_score ?? null,
    completedHeats,
    totalHeats,
    currentHeat: currentHeat || (status === "live" ? completedHeats + 1 : 0),
    latestHeatLabel: live?.latestHeatLabel || live?.lastHeatLabel || (completedHeats > 0 ? `Heat ${completedHeats}` : ""),
    latestHeatSummary:
      live?.latestHeatSummary || live?.lastHeatSummary || live?.lastEvent || live?.latestEvent || live?.summary || "",
    updatedAt: live?.updatedAt || live?.updated_at || null,
    homeLineup,
    awayLineup,
  };
}

function StatusBadge({ status }) {
  if (status === "live") {
    return (
      <Badge className="gap-1 border border-red-500/40 bg-red-500/10 text-red-600 dark:text-red-300">
        <Radio className="h-3.5 w-3.5" />
        Live
      </Badge>
    );
  }
  if (status === "finished") {
    return (
      <Badge variant="secondary" className="gap-1">
        <Trophy className="h-3.5 w-3.5" />
        Avslutad
      </Badge>
    );
  }
  return (
    <Badge variant="outline" className="gap-1">
      <CalendarDays className="h-3.5 w-3.5" />
      Kommande
    </Badge>
  );
}

function LineupCard({ title, entries }) {
  return (
    <Card className="border-border/70 bg-card/80">
      <CardHeader className="pb-3">
        <CardTitle className="flex items-center gap-2 text-lg">
          <Users className="h-4 w-4" />
          {title}
        </CardTitle>
      </CardHeader>
      <CardContent>
        {entries.length > 0 ? (
          <ul className="space-y-2 text-sm">
            {entries.map((entry, index) => {
              const lineupNo = entry?.lineup_no ?? null;
              const name = entry?.name || entry?.rider_name || entry?.rider || "Förare";
              return (
                <li key={`${title}-${lineupNo ?? index}-${name}`} className="flex items-center gap-3 rounded-xl border border-border/50 bg-background/60 px-3 py-2">
                  {lineupNo ? (
                    <span className="inline-flex h-7 w-7 items-center justify-center rounded-full bg-primary/10 text-xs font-semibold text-primary">
                      {lineupNo}
                    </span>
                  ) : null}
                  <span>{name}</span>
                </li>
              );
            })}
          </ul>
        ) : (
          <div className="rounded-xl border border-dashed border-border/70 bg-background/40 p-4 text-sm text-muted-foreground">
            Laguppställningen visas här när live- eller officiell lineup finns tillgänglig.
          </div>
        )}
      </CardContent>
    </Card>
  );
}

export default function LiveDetailPage() {
  const navigate = useNavigate();
  const { officialId } = useParams();
  const { data, isLoading, error } = useOfficialMatch(officialId, {
    enabled: !!officialId,
    staleTime: 60 * 1000,
  });

  const model = useMemo(() => (data ? getLiveDetailModel(data) : null), [data]);
  const progressValue = model
    ? Math.max(0, Math.min(100, Math.round((model.completedHeats / model.totalHeats) * 100)))
    : 0;
  const hasScore = model && model.homeScore !== null && model.awayScore !== null;

  if (isLoading) {
    return (
      <div className="space-y-6">
        <Button variant="ghost" size="sm" onClick={() => navigate("/live")} className="w-fit px-0">
          <ArrowLeft className="mr-2 h-4 w-4" />
          Till live
        </Button>
        <Card>
          <CardContent className="p-8 text-sm text-muted-foreground">Laddar livevy…</CardContent>
        </Card>
      </div>
    );
  }

  if (error || !model) {
    return (
      <div className="space-y-6">
        <Button variant="ghost" size="sm" onClick={() => navigate("/live")} className="w-fit px-0">
          <ArrowLeft className="mr-2 h-4 w-4" />
          Till live
        </Button>
        <Card>
          <CardContent className="p-8 text-sm text-muted-foreground">
            Kunde inte ladda livevyn för matchen.
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <Button variant="ghost" size="sm" onClick={() => navigate("/live")} className="w-fit px-0">
        <ArrowLeft className="mr-2 h-4 w-4" />
        Till live
      </Button>

      <Card className="overflow-hidden border-primary/20 bg-gradient-to-br from-card via-card to-primary/5">
        <CardHeader className="gap-4">
          <div className="flex items-start justify-between gap-4">
            <div>
              <div className="flex items-center gap-2 text-primary">
                <Radio className="h-4 w-4" />
                <span className="text-xs font-semibold uppercase tracking-[0.2em]">Livevy</span>
              </div>
              <CardTitle className="mt-3 text-3xl">
                {model.home}
                <span className="px-2 text-muted-foreground">vs</span>
                {model.away}
              </CardTitle>
              <CardDescription className="mt-3 flex flex-wrap items-center gap-x-4 gap-y-2">
                <span className="inline-flex items-center gap-1.5">
                  <CalendarDays className="h-3.5 w-3.5" />
                  {model.startInfo.dateLabel}
                </span>
                {model.startInfo.timeLabel ? (
                  <span className="inline-flex items-center gap-1.5">
                    <Clock3 className="h-3.5 w-3.5" />
                    {model.startInfo.timeLabel}
                  </span>
                ) : null}
                {model.venue ? (
                  <span className="inline-flex items-center gap-1.5">
                    <MapPin className="h-3.5 w-3.5" />
                    {model.venue}
                  </span>
                ) : null}
              </CardDescription>
            </div>
            <StatusBadge status={model.status} />
          </div>
        </CardHeader>

        <CardContent className="grid gap-4 lg:grid-cols-3">
          <div className="rounded-2xl border border-border/60 bg-background/70 p-4">
            <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Status</p>
            <p className="mt-2 text-xl font-semibold">{model.statusText}</p>
            <p className="mt-2 text-sm text-muted-foreground">
              Den här ytan är avsedd för matchens övergripande livefas och framtida statusinformation.
            </p>
          </div>

          <div className="rounded-2xl border border-border/60 bg-background/70 p-4">
            <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Ställning</p>
            <p className="mt-2 text-3xl font-bold">{hasScore ? `${model.homeScore} - ${model.awayScore}` : "–"}</p>
            <p className="mt-2 text-sm text-muted-foreground">
              Resultatet läser från samma modell som framtida livefeed ska uppdatera.
            </p>
          </div>

          <div className="rounded-2xl border border-border/60 bg-background/70 p-4">
            <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Heatprogress</p>
            <div className="mt-3 space-y-2">
              <Progress value={progressValue} className="h-2" />
              <div className="flex flex-wrap items-center gap-3 text-xs text-muted-foreground">
                <span className="inline-flex items-center gap-1">
                  <Flag className="h-3.5 w-3.5" />
                  Aktuellt heat: {model.currentHeat || "–"}
                </span>
                <span className="inline-flex items-center gap-1">
                  <Activity className="h-3.5 w-3.5" />
                  Körda heat: {model.completedHeats}/{model.totalHeats}
                </span>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      <div className="grid gap-4 lg:grid-cols-[minmax(0,1.15fr)_minmax(0,0.85fr)]">
        <Card className="border-border/70 bg-card/80">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-lg">
              <Signal className="h-4 w-4" />
              Senaste heat och liveflöde
            </CardTitle>
            <CardDescription>
              Här ska senaste heat, senaste händelse och framtida livekommentarer visas.
            </CardDescription>
          </CardHeader>
          <CardContent>
            {model.latestHeatSummary ? (
              <div className="rounded-2xl border border-border/60 bg-background/70 p-4">
                <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                  {model.latestHeatLabel || "Senaste heat"}
                </p>
                <p className="mt-2 text-sm">{model.latestHeatSummary}</p>
                <p className="mt-3 text-xs text-muted-foreground">
                  Uppdaterad{" "}
                  {model.updatedAt
                    ? new Date(model.updatedAt).toLocaleTimeString("sv-SE", {
                        hour: "2-digit",
                        minute: "2-digit",
                      })
                    : "nyligen"}
                </p>
              </div>
            ) : (
              <div className="rounded-2xl border border-dashed border-border/70 bg-background/40 p-5 text-sm text-muted-foreground">
                Livehändelser är ännu inte kopplade. Layouten är klar för senaste heat, matchtempo och löpande kommentarer.
              </div>
            )}
          </CardContent>
        </Card>

        <div className="space-y-4">
          <LineupCard title={`${model.home} – laguppställning`} entries={model.homeLineup} />
          <LineupCard title={`${model.away} – laguppställning`} entries={model.awayLineup} />
        </div>
      </div>
    </div>
  );
}
